import {
  type FC,
  type Key,
  memo,
  type PropsWithChildren,
  useCallback,
  useContext,
  useDeferredValue,
  useEffect,
  useMemo,
  useState,
} from "react";
import { ErrorBoundary, useErrorBoundary } from "react-error-boundary";
import { useNavigate } from "react-router";
import { Item, Section } from "react-stately";
import {
  ActionButton,
  HeaderBar,
  HeaderDivider,
  Icons,
  ItemTile,
  ListBoxWithChildren,
  LoadingSpinner,
  MenuOptionItem,
  PanelGroup,
  PanelOrientation,
  PanelPosition,
  SearchField,
  Select,
  Spacer,
  TriggerButton,
  VStack,
} from "widgets";

import { matchFilter, parseFilterExpression } from "@/app-utils/filter.ts";
import { useError } from "@/app-utils/graphql/useError.ts";
import { generateAttributeStringCompare } from "@/app-utils/string.ts";
import { ToggleFiltersButton } from "@/modules/core/components/ToggleFiltersButton.tsx";
import { FallbackPanel } from "@/modules/workflow/components/ErrorFallbackPanel.tsx";
import { Fallback } from "@/modules/workflow/components/Fallback.tsx";
import { WorkflowCards } from "@/modules/workflow/components/WorkflowCards.tsx";
import {
  useGetPublishedWorkflowsLazyQuery,
  useGetWorkflowsLazyQuery,
  type WorkflowP2TileFragment,
} from "@/modules/workflow/graphql/workflow.gql.ts";
import {
  initialWorkflowContextValue,
  WorkflowContext,
  type WorkflowContextType,
} from "@/modules/workflow/stores/workflow-context.ts";
import { SELECTABLE_WORKFLOW_TYPE, type WorkflowListData } from "@/modules/workflow/utils/types.ts";
import { ContentPlaceholder } from "@/ui-modules/core/ContentPlaceholder.tsx";

const addNewWorkflowTemplateItem = {
  id: "new-template",
  name: "New template",
} as unknown as WorkflowP2TileFragment;

// Create the provider component
const WorkflowProvider: FC<PropsWithChildren> = ({ children }) => {
  const [workflow, setWorkflow] = useState<WorkflowContextType["workflow"]>(initialWorkflowContextValue.workflow);
  const [isLoading, setLoading] = useState<WorkflowContextType["isLoading"]>(initialWorkflowContextValue.isLoading);

  // Combine the state and update functions into a single object
  const value = {
    workflow,
    setWorkflow,
    isLoading,
    setLoading,
  };

  return <WorkflowContext.Provider value={value}>{children}</WorkflowContext.Provider>;
};

export const WorkflowSelectionWithPreview: FC<{
  onOpenWorkflow?: (workflowId: number) => void;
  hideActions?: boolean;
  placeholderMessage?: string;
  previewOnly?: boolean;
  defaultSelectedWorkflowId?: number | undefined;
  onSelect?: (workflowId: number | undefined) => void;
  /** Limit listing to specific type and hide select  */
  restrictSelectableType?: SELECTABLE_WORKFLOW_TYPE;
  showNewTemplateEntry?: boolean;
}> = ({ onSelect, ...props }) => {
  const [currentSelection, setCurrentSelection] = useState<number | undefined>(props.defaultSelectedWorkflowId);

  const setCurrentSelectionHandler = useCallback(
    (workflowId: number | undefined) => {
      setCurrentSelection(workflowId);
      onSelect?.(workflowId);
    },
    [onSelect],
  );

  return (
    <WorkflowProvider>
      <PanelGroup
        key="workflows-listing"
        orientation={PanelOrientation.Horizontal}
        defaultSize={350}
        minSize={315}
        maxSize={500}
        fixedPanel={PanelPosition.Leading}
        isContinuous
        data-testid="workflows-listing"
      >
        <WorkflowListingPanel
          onOpen={props.onOpenWorkflow}
          actionItem={props.showNewTemplateEntry ? addNewWorkflowTemplateItem : undefined}
          restrictSelectableType={props.restrictSelectableType}
          selectedWorkflowId={currentSelection}
          setSelectedWorkflowId={setCurrentSelectionHandler}
        />
        <VStack spacing={0} style={{ width: "100%" }}>
          <WorkflowPageHeader onOpen={props.onOpenWorkflow} hideActions={props.hideActions} />
          <ErrorBoundary fallbackRender={Fallback}>
            <WorkflowCards
              hideActions={props.hideActions}
              placeholderMessage={props.placeholderMessage}
              previewOnly={props.previewOnly}
              selectedWorkflowId={currentSelection}
            />
          </ErrorBoundary>
        </VStack>
      </PanelGroup>
    </WorkflowProvider>
  );
};

interface WorkflowPageHeaderProps {
  hideActions?: boolean;
  onOpen?: (workflowId: number) => void;
}

const WorkflowPageHeader: FC<WorkflowPageHeaderProps> = ({ hideActions, onOpen }) => {
  const navigate = useNavigate();
  const { workflow, isLoading } = useContext(WorkflowContext);

  return (
    <HeaderBar
      title={workflow?.name ?? " "}
      isLoading={isLoading}
      asPanelHeader
      actions={
        hideActions
          ? undefined
          : [
              workflow && (
                <ActionButton
                  key="edit-workflow-action"
                  icon="account_tree"
                  onPress={() => onOpen?.(workflow.id)}
                  label="Open in Processor"
                />
              ),
              <HeaderDivider key="divider-1" />,
              <TriggerButton
                key="new-workflow-action"
                icon="add_circle"
                onPress={() => navigate("/processor")}
                label="Create New Workflow Template"
              />,
            ]
      }
    />
  );
};

enum WORKFLOW_SORTING_KEYS {
  NAME = "name",
  CREATOR = "creator",
  CREATION_DATE = "creation_date",
}

interface WorkflowListingPanelProps {
  onOpen?: (workflowId: number) => void;
  actionItem?: WorkflowP2TileFragment;
  placeholderMessage?: string;
  /** Limit listing to specific type and hide select  */
  restrictSelectableType?: SELECTABLE_WORKFLOW_TYPE;
  selectedWorkflowId: number | undefined;
  setSelectedWorkflowId: (workflowId: number | undefined) => void;
}

const WorkflowListingPanel: FC<WorkflowListingPanelProps> = (props) => {
  const [selectedWorkflowSortingKey, setWorkflowSortingKey] = useState<WORKFLOW_SORTING_KEYS>(
    WORKFLOW_SORTING_KEYS.CREATION_DATE,
  );
  const [showFilters, setShowFilters] = useState<boolean>(false);
  const [filterTerm, setFilterTerm] = useState<string>();
  const debouncedFilterText = useDeferredValue(filterTerm);

  const [showPublishedWorkflows, setShowPublishedWorkflows] = useState<SELECTABLE_WORKFLOW_TYPE>(
    props.restrictSelectableType ?? SELECTABLE_WORKFLOW_TYPE.TEMPLATE,
  );

  const reset = useCallback(() => {
    setShowFilters((toggle) => !toggle);
    setFilterTerm("");
    setWorkflowSortingKey(WORKFLOW_SORTING_KEYS.CREATION_DATE);
    setShowPublishedWorkflows(props.restrictSelectableType ?? SELECTABLE_WORKFLOW_TYPE.TEMPLATE);
  }, [props.restrictSelectableType]);

  return (
    <VStack style={{ width: "100%", backgroundColor: "var(--panel-background)" }} spacing={0}>
      <HeaderBar
        asPanelHeader
        title={"Workflows"}
        actions={[
          <Spacer key="workflow-filtering-spacer" />,
          <ToggleFiltersButton
            key="toggle-filters"
            areFiltersVisible={showFilters}
            toggleFiltersVisibility={() => {
              reset();
            }}
            data-testid="toggle-filters"
          />,
        ]}
      />
      {showFilters ? (
        <VStack spacing={10} style={{ padding: 7, paddingBottom: 13, flex: 0 }} data-testid="filters">
          <WorkflowSortingWidget
            key="workflow-sorting-widget"
            selectedWorkflowSortingKey={selectedWorkflowSortingKey}
            setWorkflowSortingKey={setWorkflowSortingKey}
          />
          <SearchField
            key="workflow-filtering-widget"
            label={"Search workflow"}
            placeholder={"Search workflow"}
            isRequired
            value={filterTerm}
            onChange={setFilterTerm}
            width={"100%"}
            icon="search"
            aria-label="Search for workflow"
            autoFocus
            name="workflow-search"
          />
          {!props.restrictSelectableType && (
            <Select
              label={"Type"}
              selectedKey={showPublishedWorkflows}
              onSelectionChange={(key: Key) => {
                props.setSelectedWorkflowId(undefined);
                setShowPublishedWorkflows(String(key) as SELECTABLE_WORKFLOW_TYPE);
              }}
              isRequired
            >
              {Object.values(SELECTABLE_WORKFLOW_TYPE).map((type) => (
                <Item key={type}>
                  <MenuOptionItem title={type} />
                </Item>
              ))}
            </Select>
          )}
        </VStack>
      ) : null}
      <Listing
        {...props}
        selectedWorkflowSortingKey={selectedWorkflowSortingKey}
        filterText={debouncedFilterText}
        showPublishedForPlaybookWorkflows={showPublishedWorkflows === SELECTABLE_WORKFLOW_TYPE.PUBLISHED}
        defaultSelectedWorkflowId={props.selectedWorkflowId}
        onSelectWorkflow={props.setSelectedWorkflowId}
      />
    </VStack>
  );
};

interface ListingProps {
  filterText?: string;
  selectedWorkflowSortingKey: WORKFLOW_SORTING_KEYS;
  onOpen?: (workflowId: number) => void;
  actionItem?: WorkflowP2TileFragment;
  showPublishedForPlaybookWorkflows?: boolean;
  defaultSelectedWorkflowId?: number;
  onSelectWorkflow: (workflowId: number | undefined) => void;
}

const Listing = memo(function Listing({ onSelectWorkflow, ...props }: ListingProps) {
  const [getWorkflows, { loading: loadingWorkflows, error: getWorkflowsError }] = useGetWorkflowsLazyQuery();
  const [getPublishedWorkflows, { loading: loadingPubWorkflows, error: getPubWorkflowsError }] =
    useGetPublishedWorkflowsLazyQuery();
  void useError(getWorkflowsError);
  void useError(getPubWorkflowsError);

  const { showBoundary } = useErrorBoundary();

  const loading = loadingWorkflows || loadingPubWorkflows;
  const [workflows, setWorkflows] = useState<WorkflowP2TileFragment[]>([]);
  const loadWorkflows = useCallback(() => {
    if (props.showPublishedForPlaybookWorkflows) {
      return getPublishedWorkflows()
        .then((result) => setWorkflows(result.data?.workflow.publishedWorkflows ?? []))
        .catch(showBoundary);
    }

    return getWorkflows()
      .then((result) => setWorkflows(result.data?.workflow?.listWorkflows ?? []))
      .catch(showBoundary);
  }, [props.showPublishedForPlaybookWorkflows, getWorkflows, showBoundary, getPublishedWorkflows]);

  const sortedFilteredWorkflows = useMemo<WorkflowListData[]>(() => {
    let sortedFilteredWorkflows = workflows.filter((workflow) => workflow.id !== props.actionItem?.id) ?? [];

    if (workflows) {
      if (props.filterText) {
        sortedFilteredWorkflows = workflows.filter((workflow: WorkflowListData) =>
          matchFilter(
            [workflow.name, workflow.description ?? "", workflow.creator?.display ?? ""],
            parseFilterExpression(props.filterText!),
          ),
        );

        if (sortedFilteredWorkflows.length === 0) {
          onSelectWorkflow(undefined);
        }
      }

      switch (props.selectedWorkflowSortingKey) {
        case WORKFLOW_SORTING_KEYS.NAME: {
          sortedFilteredWorkflows = sortedFilteredWorkflows.sort(generateAttributeStringCompare(["name"], true, true));
          break;
        }
        case WORKFLOW_SORTING_KEYS.CREATOR: {
          sortedFilteredWorkflows = sortedFilteredWorkflows.sort(
            generateAttributeStringCompare(["creator.display"], true, true),
          );
          break;
        }
        default: {
          sortedFilteredWorkflows = sortedFilteredWorkflows.sort(
            generateAttributeStringCompare(["created"], false, true),
          );
          break;
        }
      }
    }

    if (props.actionItem) {
      sortedFilteredWorkflows.unshift(props.actionItem);
    }

    return sortedFilteredWorkflows;
  }, [workflows, props.actionItem, props.filterText, props.selectedWorkflowSortingKey, onSelectWorkflow]);

  useEffect(() => {
    setWorkflows([]);
    void loadWorkflows();
  }, [loadWorkflows]);

  useEffect(
    () => () => {
      onSelectWorkflow(undefined);
    },
    [onSelectWorkflow],
  );

  return (
    <ErrorBoundary FallbackComponent={FallbackPanel}>
      <VStack style={{ overflow: "auto", height: "100%" }}>
        {!sortedFilteredWorkflows?.length && props.filterText ? (
          <ContentPlaceholder>No workflow templates found</ContentPlaceholder>
        ) : null}
        {loading ? (
          <>
            <Spacer />
            <LoadingSpinner />
            <Spacer />
          </>
        ) : (
          <ListBoxWithChildren
            items={sortedFilteredWorkflows}
            selectionMode="single"
            selectionBehavior="replace"
            disallowEmptySelection
            onAction={(key) => {
              props.onOpen?.(Number(key));
            }}
            onSelectionChange={(keys) => {
              const selectedWorkflowId = (keys as Set<Key>).values().next().value; // could be 'all'
              if (selectedWorkflowId != undefined && selectedWorkflowId !== "all") {
                // We defined the create new workflow entry with id=null, being stringified in the list box.
                onSelectWorkflow(
                  selectedWorkflowId == undefined || selectedWorkflowId === addNewWorkflowTemplateItem.id
                    ? undefined
                    : Number(selectedWorkflowId),
                );
              }
            }}
            selectedKeys={
              props.defaultSelectedWorkflowId == null
                ? props.actionItem
                  ? new Set([String(props.actionItem.id)])
                  : undefined
                : new Set([String(props.defaultSelectedWorkflowId)])
            }
            style={{ gap: 5, padding: 7 }}
          >
            {(workflow) => (
              <Item key={String(workflow.id)}>
                <ItemTile
                  icon={<img src={Icons.Tiles.Workflow} width={24} height={30} alt="" aria-hidden />}
                  name={workflow.name}
                  id={workflow.id}
                  userInfoTag={workflow.creator?.display}
                  dateInfoTag={workflow.modified ?? workflow.created}
                />
              </Item>
            )}
          </ListBoxWithChildren>
        )}
      </VStack>
    </ErrorBoundary>
  );
});

interface WorkflowSortingWidgetProps {
  selectedWorkflowSortingKey: WORKFLOW_SORTING_KEYS;
  setWorkflowSortingKey: (key: WORKFLOW_SORTING_KEYS) => void;
}

const WorkflowSortingWidget: FC<WorkflowSortingWidgetProps> = (props) => {
  return (
    <Select
      label={"Sort by"}
      onSelectionChange={props.setWorkflowSortingKey as (key: Key) => void}
      defaultSelectedKey={WORKFLOW_SORTING_KEYS.CREATION_DATE}
      selectedKey={props.selectedWorkflowSortingKey}
      isRequired
      width={110}
      icon="sort"
      name="workflow-sorting"
    >
      <Section title="Sort By">
        <Item key={WORKFLOW_SORTING_KEYS.CREATION_DATE} textValue="Date">
          <MenuOptionItem title="Date Created" icon="calendar_today" />
        </Item>
        <Item key={WORKFLOW_SORTING_KEYS.NAME} textValue="Name">
          <MenuOptionItem title="Name" icon="sort_by_alpha" />
        </Item>
        <Item key={WORKFLOW_SORTING_KEYS.CREATOR} textValue="Owner">
          <MenuOptionItem title="Owner" icon="person" />
        </Item>
      </Section>
    </Select>
  );
};
